import { Action } from 'kbar';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CLIENT_DETAILS_PAGE } from 'src/constants/pathConsts';
import { PortalConfigContext } from 'src/context';
import { RouteContext } from 'src/context/routeContext';
import history from 'src/history';
import { useKeyboardShortcuts } from 'src/hooks/useKeyboardShortcuts';
import { useRouteAccess } from 'src/routes/routeAccess';
import { RootState } from 'src/store/reduxTypes';

export const useClientsCommandActions = () => {
  const { routeAccess } = useRouteAccess();
  const { t } = useTranslation();
  const { pathname } = React.useContext(RouteContext);
  const isClientDetailsPage = pathname.includes(CLIENT_DETAILS_PAGE.path);
  const availableClients = useSelector(
    (state: RootState) => state.clients.activeClients,
  );

  const companies = useSelector((state: RootState) => state.clients.companies);
  const portalConfig = React.useContext(PortalConfigContext);
  const areCompaniesDisabled = Boolean(portalConfig.features?.disableCompanies);
  const goToClientDetails = (clientId: string) => {
    history.push(`${CLIENT_DETAILS_PAGE.path}/${clientId}`);
  };

  /**
   * This transform the loaded client into actions
   * to be registered as kbar actions.
   */
  const clientListActions: Array<Action> = React.useMemo(
    () =>
      availableClients.map((client) => {
        const {
          fallbackColor,
          givenName: firstName,
          familyName: lastName,
          email,
          avatarImageUrl,
        } = client.fields;

        const clientCompanies = companies.filter((company) =>
          client.fields.companyIds?.includes(company.id),
        );
        let companyName = '';

        if (!areCompaniesDisabled) {
          if (clientCompanies.length === 1) {
            companyName = clientCompanies.at(0)?.fields.name ?? '';
          } else if (clientCompanies.length > 1) {
            companyName = `${clientCompanies.length} companies`;
          }
        }

        const clientAction = {
          id: client.id,
          name: `${firstName} ${lastName}`,
          // clients actions are registered under open-client action
          // that means they only render when open-client action is clicked
          parent: 'open-client',
          keywords: `${firstName} ${lastName}  ${companyName || ''} ${email}`,
          perform: () => goToClientDetails(client.id),
          // this holds the additional user data to be rendered in the action menu
          additionalData: {
            pictureUrl: avatarImageUrl,
            companyName: companyName !== 'No company' ? companyName : '',
            fallbackColor,
          },
        };

        return clientAction;
      }),
    [availableClients, companies, areCompaniesDisabled],
  );
  useKeyboardShortcuts(
    // register open client command/shortcut only when user has permission to access the page
    // and there are clients available in the portal.
    routeAccess.allowClientDetailsPage && availableClients.length > 0
      ? [
          {
            id: 'open-client',
            name: `${t(($) => $.actions.open_client)}...`,
            section: isClientDetailsPage ? 'General' : undefined,
            keywords: 'view open client details',
            shortcut: ['o'],
          },
          ...clientListActions,
        ]
      : [],
  );
};
